import React, { useState } from 'react';
import { useRouteError } from 'react-router-dom';
import { Button, Card, Typography, Alert, Collapse } from 'antd';
import { 
    RefreshCw, 
    Home, 
    Copy, 
    Check, 
    Key, 
    Terminal, 
    ShieldAlert 
} from 'lucide-react';

const { Text, Title } = Typography;

export default function RootErrorFallback() {
    const error = useRouteError();
    const [copied, setCopied] = useState(false);

    const errorMessage = error?.message || error?.statusText || (typeof error === 'string' ? error : 'An unexpected application error occurred.');
    const errorStack = error?.stack || '';
    const isFirebaseConfigError = errorMessage.includes('Firebase is not configured') || errorMessage.includes('VITE_FIREBASE');

    const handleCopyDetails = () => {
        const details = `[School CRM Error]\nMessage: ${errorMessage}\nStack: ${errorStack || 'N/A'}\nURL: ${window.location.href}\nTime: ${new Date().toISOString()}`;
        navigator.clipboard.writeText(details);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleReload = () => {
        window.location.reload();
    };

    const handleGoHome = () => {
        window.location.href = '/';
    };

    return (
        <div
            style={{
                minHeight: '100vh',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
                color: '#f8fafc',
                padding: '24px',
                boxSizing: 'border-box',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}
        >
            <Card
                style={{
                    maxWidth: '720px',
                    width: '100%',
                    background: 'rgba(30, 41, 59, 0.85)',
                    backdropFilter: 'blur(16px)',
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '16px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                    color: '#f8fafc',
                    overflow: 'hidden'
                }}
                bodyStyle={{ padding: '32px' }}
            >
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <div
                        style={{
                            width: '64px',
                            height: '64px',
                            borderRadius: '50%',
                            background: isFirebaseConfigError ? 'rgba(245, 158, 11, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 16px auto',
                            border: `1px solid ${isFirebaseConfigError ? 'rgba(245, 158, 11, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                        }}
                    >
                        {isFirebaseConfigError ? (
                            <Key size={32} color="#f59e0b" />
                        ) : (
                            <ShieldAlert size={32} color="#ef4444" />
                        )}
                    </div>

                    <Title level={2} style={{ color: '#ffffff', margin: 0, fontWeight: 700 }}>
                        {isFirebaseConfigError ? 'Firebase Configuration Required' : 'Application Error'}
                    </Title>
                    <Text style={{ color: '#94a3b8', fontSize: '15px', display: 'block', marginTop: '8px' }}>
                        {isFirebaseConfigError 
                            ? 'The application could not connect to Firebase because environment variables are missing.' 
                            : 'An unhandled exception occurred while rendering this page.'}
                    </Text>
                </div>

                {isFirebaseConfigError ? (
                    <Alert
                        type="warning"
                        showIcon={false}
                        style={{
                            background: 'rgba(245, 158, 11, 0.08)',
                            border: '1px solid rgba(245, 158, 11, 0.25)',
                            borderRadius: '12px',
                            marginBottom: '24px',
                            color: '#fde68a'
                        }}
                        message={
                            <div style={{ padding: '4px 0' }}>
                                <Text style={{ color: '#fbbf24', fontWeight: 600, fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Terminal size={18} /> How to fix this build issue:
                                </Text>
                                <ul style={{ margin: '12px 0 4px 20px', padding: 0, color: '#cbd5e1', lineHeight: '1.6', fontSize: '14px' }}>
                                    <li>
                                        <strong>Local Development:</strong> Ensure your <code>.env</code> file includes all <code>VITE_FIREBASE_*</code> variables (see <code>.env.example</code>).
                                    </li>
                                    <li>
                                        <strong>GitHub Actions / CI-CD:</strong> Set repository Secrets in GitHub for <code>VITE_FIREBASE_API_KEY</code>, <code>VITE_FIREBASE_PROJECT_ID</code>, etc.
                                    </li>
                                </ul>
                            </div>
                        }
                    />
                ) : (
                    <Alert
                        type="error"
                        showIcon={false}
                        style={{
                            background: 'rgba(239, 68, 68, 0.08)',
                            border: '1px solid rgba(239, 68, 68, 0.25)',
                            borderRadius: '12px',
                            marginBottom: '24px',
                            color: '#fca5a5'
                        }}
                        message={
                            <Text style={{ color: '#f87171', fontWeight: 500, fontSize: '14px' }}>
                                {errorMessage}
                            </Text>
                        }
                    />
                )}

                {errorStack && (
                    <Collapse
                        ghost
                        style={{ marginBottom: '24px' }}
                        items={[
                            {
                                key: '1',
                                label: <Text style={{ color: '#64748b', fontSize: '13px' }}>View Technical Diagnostics & Stack Trace</Text>,
                                children: (
                                    <div
                                        style={{
                                            background: '#090d16',
                                            padding: '12px 16px',
                                            borderRadius: '8px',
                                            maxHeight: '180px',
                                            overflowY: 'auto',
                                            fontSize: '12px',
                                            fontFamily: 'monospace',
                                            color: '#cbd5e1',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            border: '1px solid rgba(255,255,255,0.05)'
                                        }}
                                    >
                                        {errorMessage}
                                        {'\n\n'}
                                        {errorStack}
                                    </div>
                                )
                            }
                        ]}
                    />
                )}

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Button
                        type="primary"
                        size="large"
                        icon={<RefreshCw size={16} />}
                        onClick={handleReload}
                        style={{
                            borderRadius: '8px',
                            background: '#3b82f6',
                            borderColor: '#3b82f6',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        Reload Application
                    </Button>

                    <Button
                        size="large"
                        icon={<Home size={16} />}
                        onClick={handleGoHome}
                        style={{
                            borderRadius: '8px',
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderColor: 'rgba(255, 255, 255, 0.15)',
                            color: '#f8fafc',
                            fontWeight: 500,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        Return Home
                    </Button>

                    <Button
                        size="large"
                        icon={copied ? <Check size={16} color="#10b981" /> : <Copy size={16} />}
                        onClick={handleCopyDetails}
                        style={{
                            borderRadius: '8px',
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderColor: 'rgba(255, 255, 255, 0.15)',
                            color: copied ? '#10b981' : '#f8fafc',
                            fontWeight: 500,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        {copied ? 'Copied Details' : 'Copy Error Details'}
                    </Button>
                </div>
            </Card>
        </div>
    );
}
